/**
 * Dataset 조사 사실의 표시 문구 — 묶음 · 칸 · 어휘 · 근거 · 보낸 곳의 이름, 검사 문제와 오류의 문장.
 *
 * 섹션과 점검 화면의 요약이 같은 이름을 쓰도록 이 파일 한 곳이 소유한다(규칙 §11).
 *
 * Owns:
 * - 사실의 사용자 문구, 자료 없음 표기, 서버 값을 따랐을 때의 알림 문장.
 *
 * Does not own:
 * - 규칙 — `domain/datasetFacts.ts`. 공통 오류 문구 — `datasetFormatters.ts`(여기서 이어 쓴다).
 */
import { isHttpClientError } from "@/shared/http";
import type {
  AnyFact,
  FactBasis,
  FactClient,
  FactEventAction,
  FactGroup,
  FactIssueCode,
  FactPath,
} from "../domain/datasetFacts";
import { resolveDatasetErrorMessage } from "./datasetFormatters";

/** 자료가 없는 칸의 표기. 비워 두지 않고 다음 행동을 함께 말한다. */
export const FACT_UNKNOWN_LABEL = "자료 없음 — 직접 조사";

export const FACT_GROUP_LABEL: Record<FactGroup, string> = {
  end_pose: "끝점 기준점·좌표계",
  hand_axes: "손 축 규약",
  gripper: "그리퍼 방향·끝점 값",
  defects: "데이터 결함",
};

export const FACT_PATH_LABEL: Record<FactPath, string> = {
  "end_pose.point": "끝점 기준점",
  "end_pose.frame": "기준 좌표계",
  "end_pose.quaternion_order": "quaternion 순서",
  "end_pose.position_unit": "위치 단위",
  "hand_axes.approach": "접근 축(approach)",
  "hand_axes.up": "위 축(up)",
  "gripper.channel": "그리퍼 채널",
  "gripper.open_value": "열림 값",
  "gripper.closed_value": "닫힘 값",
  "defects.quaternion_norm": "quaternion 노름 결함",
  "defects.channel_names": "채널 이름 결함",
};

/** 어휘 값의 이름. 축 · 순서 · 단위는 기호가 이미 이름이라 그대로 쓴다. */
const CHOICE_LABEL: Readonly<Record<string, string>> = {
  flange: "플랜지",
  sigmakit_closed_tcp: "닫힘 TCP",
  other_fixed: "다른 고정 기준점",
  grip_center: "파지 중심(개폐에 따라 변함)",
  present: "있음",
  absent: "없음",
};

export const FACT_BASIS_LABEL: Record<FactBasis, string> = {
  source_declared: "원본 선언",
  documented: "공식 자료",
  measured: "실측",
  inferred: "추론",
  assumed: "가정",
};

export const FACT_CLIENT_LABEL: Record<FactClient, string> = {
  fe: "화면",
  mcp: "MCP",
  unknown: "알 수 없는 곳",
};

export const FACT_ACTION_LABEL: Record<FactEventAction, string> = {
  proposed: "제안",
  confirmed: "확정",
  discarded: "제안 폐기",
};

export function formatFactChoice(value: string): string {
  return CHOICE_LABEL[value] ?? value;
}

/** 칸 값 한 줄. 자료가 없으면 다음 행동을 말한다. */
export function formatFactValue(fact: AnyFact): string {
  if (fact.value === null) {
    return FACT_UNKNOWN_LABEL;
  }
  return typeof fact.value === "number" ? String(fact.value) : formatFactChoice(fact.value);
}

const ISSUE_MESSAGE: Record<FactIssueCode, string> = {
  partial: "값 · 근거 · 출처 메모를 함께 채우거나 함께 비워 주세요.",
  note_blank: "출처 메모에 글자를 적어 주세요.",
  note_bad_char: "출처 메모에 저장할 수 없는 문자가 있습니다. 그 문자를 지워 주세요.",
  note_too_long: "출처 메모는 2000자 이하로 적어 주세요.",
  text_blank: "이름에 글자를 적어 주세요.",
  text_bad_char: "이름에 저장할 수 없는 문자가 있습니다. 그 문자를 지워 주세요.",
  text_too_long: "이름이 너무 깁니다. 줄여서 적어 주세요.",
  not_finite: "유한한 숫자를 적어 주세요.",
  axes_parallel: "위 축은 접근 축과 다른 축이어야 합니다.",
  gripper_same: "닫힘 값은 열림 값과 달라야 합니다.",
};

export function formatFactIssue(code: FactIssueCode): string {
  return ISSUE_MESSAGE[code];
}

export const FACTS_REVISION_CONFLICT_CODE = "dataset_facts.revision_conflict";

/** 제안 · 확정 · 폐기 실패의 문구. 422 는 칸을 고치라고 말한다. */
export function resolveFactsCommandMessage(error: unknown): string {
  if (isHttpClientError(error) && error.code === "request.validation_failed") {
    return "서버가 조사 사실 값을 받지 않았습니다. 칸의 값을 확인한 뒤 다시 시도해 주세요.";
  }
  return resolveDatasetErrorMessage(error);
}

/** 서버 값을 따른 계기. `conflict` 는 이 화면의 쓰기가 409 로 거부됐다는 뜻이다. */
export type FactsFollowReason = "observed" | "conflict";

export function followFactsMessage(reason: FactsFollowReason, discarded: boolean): string {
  const head =
    reason === "conflict"
      ? "다른 곳에서 조사 사실이 먼저 바뀌어 요청을 반영하지 않았습니다. 서버 값을 다시 불러왔습니다."
      : "다른 곳에서 조사 사실이 바뀌어 다시 불러왔습니다.";
  return discarded ? `${head} 저장하지 않은 편집은 버렸습니다.` : head;
}

/** 409 뒤 다시 받기마저 실패했을 때. 편집은 남기고 다음 행동을 말한다. */
export const FACTS_CONFLICT_RELOAD_FAILED =
  "다른 곳에서 조사 사실이 먼저 바뀌어 요청을 반영하지 않았습니다. 서버 값을 다시 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.";
